import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";

import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc
} from "firebase/firestore";

import { db } from "../utils/firebase";
import { useAuth } from "./AuthContext";
import {
  JOB_BANK_ROLES,
  RoleDenied,
  isAdmin,
  redirectToJobBankLogin,
  useJobBankRole
} from "./jobBankRole";

import "./accessRequests.css";

function formatDate(value) {
  if (!value) {
    return "";
  }

  const date = value?.toDate
    ? value.toDate()
    : new Date(value);

  if (isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}


export default function AccessRequests() {
  const { currentUser, loadingProfile } = useAuth();
  const { role, roleLoading } = useJobBankRole(currentUser);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [filter, setFilter] = useState("Pending");

  const canViewRequests = isAdmin(role);

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);

      const requestQuery = query(
        collection(db, "accessRequests"),
        orderBy("createdAt", "desc")
      );

      const snapshot = await getDocs(requestQuery);

      setRequests(
        snapshot.docs.map(requestDoc => ({
          id: requestDoc.id,
          ...requestDoc.data()
        }))
      );
    } catch (error) {
      console.error("Unable to load access requests:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (loadingProfile || roleLoading) {
      return;
    }

    if (!currentUser) {
      redirectToJobBankLogin("/accessRequests");
      return;
    }

    if (canViewRequests) {
      loadRequests();
    } else {
      setLoading(false);
    }
  }, [
    currentUser,
    loadingProfile,
    roleLoading,
    canViewRequests,
    loadRequests
  ]);

  const filteredRequests = useMemo(() => {
    if (filter === "All") {
      return requests;
    }

    return requests.filter(request =>
      (request.status || "Pending") === filter
    );
  }, [requests, filter]);

  async function approveRequest(request) {
    try {
      setUpdatingId(request.id);

      const userRef = doc(db, "users", request.uid);
      const requestRef = doc(db, "accessRequests", request.id);

      await updateDoc(userRef, {
        jobBankRole: request.requestedRole,
        updatedAt: serverTimestamp()
      });

      await updateDoc(requestRef, {
        status: "Approved",
        reviewedAt: serverTimestamp(),
        reviewedBy: currentUser.uid,
        reviewedByEmail: currentUser.email || "",
        grantedRole: request.requestedRole,
        updatedAt: serverTimestamp()
      });

      await loadRequests();
    } catch (error) {
      console.error("Unable to approve access request:", error);
      alert("Unable to approve access request.");
    } finally {
      setUpdatingId(null);
    }
  }

  async function denyRequest(request) {
    try {
      setUpdatingId(request.id);

      const requestRef = doc(db, "accessRequests", request.id);

      await updateDoc(requestRef, {
        status: "Denied",
        reviewedAt: serverTimestamp(),
        reviewedBy: currentUser.uid,
        reviewedByEmail: currentUser.email || "",
        updatedAt: serverTimestamp()
      });

      await loadRequests();
    } catch (error) {
      console.error("Unable to deny access request:", error);
      alert("Unable to deny access request.");
    } finally {
      setUpdatingId(null);
    }
  }

  if (loadingProfile || roleLoading || loading) {
    return (
      <div className="accessRequestsPage">
        <h2>Loading Access Requests...</h2>
      </div>
    );
  }

  if (!canViewRequests) {
    return (
      <RoleDenied
        title="Admin Access Required"
        message="Only users with the Job Bank Role of Admin can view and approve access requests."
        requestedRole={JOB_BANK_ROLES.ADMIN}
        requestedAccess="Access Request Administration"
        requestKey="access-request-admin"
        sourcePath="/accessRequests"
      />
    );
  }

  return (
    <div className="accessRequestsPage">
      <div className="accessRequestsHeader">
        <div>
          <p className="accessRequestEyebrow">
            The iSpeakWell Project Job Bank
          </p>
          <h1>Access Requests</h1>
          <p>
            Review pending role requests and grant the requested Job Bank Role.
          </p>
        </div>

        <button onClick={loadRequests}>
          Refresh
        </button>
      </div>

      <div className="accessRequestFilters">
        {["Pending", "Approved", "Denied", "All"].map(status => (
          <button
            key={status}
            className={filter === status ? "active" : ""}
            onClick={() => setFilter(status)}
          >
            {status}
          </button>
        ))}
      </div>

      <div className="accessRequestList">
        {filteredRequests.length === 0 ? (
          <div className="accessRequestEmpty">
            No access requests found.
          </div>
        ) : (
          filteredRequests.map(request => (
            <div
              className="accessRequestReviewCard"
              key={request.id}
            >
              <div className="accessRequestReviewMain">
                <h2>
                  {request.firstName} {request.lastName}
                </h2>

                <p>
                  <strong>Email:</strong> {request.email}
                </p>
                <p>
                  <strong>Requested Access:</strong> {request.requestedAccess}
                </p>
                <p>
                  <strong>Requested Role:</strong> {request.requestedRole}
                </p>
                <p>
                  <strong>Current Role:</strong> {request.currentJobBankRole || "No Job Bank Role"}
                </p>
                <p>
                  <strong>Requested:</strong> {formatDate(request.createdAt)}
                </p>
              </div>

              <div className="accessRequestUserDetails">
                <span><strong>Phone:</strong> {request.phone || "Not set"}</span>
                <span><strong>Location:</strong> {[request.city, request.province].filter(Boolean).join(", ") || "Not set"}</span>
                <span><strong>Job Role:</strong> {request.jobRole || "Not set"}</span>
                <span><strong>Primary Occupation:</strong> {request.primaryOccupation || "Not set"}</span>
                <span><strong>Goal:</strong> {request.primaryEmploymentGoals || "Not set"}</span>
              </div>

              <div className="accessRequestReviewActions">
                <span className={`requestStatus ${(request.status || "Pending").toLowerCase()}`}>
                  {request.status || "Pending"}
                </span>

                {(request.status || "Pending") === "Pending" && (
                  <>
                    <button
                      onClick={() => approveRequest(request)}
                      disabled={updatingId === request.id}
                    >
                      {updatingId === request.id
                        ? "Approving..."
                        : "Approve"}
                    </button>

                    <button
                      onClick={() => denyRequest(request)}
                      disabled={updatingId === request.id}
                    >
                      Deny
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
